import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowDownToLine, ArrowUpToLine, Copy, Lock, LockOpen, Minus, MoreHorizontal,
  Move, Plus, RotateCcw, RotateCw, Trash2,
} from "lucide-react";
import { t as translateStatic, useT } from "@/src/i18n";
import { Camera, snapPointToGrid, snapToGrid } from "@/src/core/engine/Camera";
import { axialToWorld, hexKey, worldToAxial } from "@/src/core/engine/HexGrid";
import { mapContentBounds, renderMapScene } from "@/src/core/engine/Renderer";
import { getStampDefinition } from "@/src/core/engine/stampCatalog";
import { geometryCentre, placementCentreFor, placementModeFor, wallPlacementFor } from "@/src/core/engine/snapping";
import type { PlacementMode } from "@/src/core/engine/snapping";
import { floorForMode, hexesForMode, objectsForMode } from "@/src/core/mapMode";
import { ROOM_KEY_ANCHOR_ASSET } from "@/src/core/roomKeys";
import {
  corridorToPolygon,
  ellipseToPolygon,
  hexagonToPolygon,
  normaliseRect,
  pointsToPolygon,
  rectToPolygon,
} from "@/src/core/geometry/boolean";
import { useMapStore } from "@/src/store/useMapStore";
import type {
  MapObject,
  Point,
  Polygon,
  ShapeTool,
  TerrainBrush,
  Viewport,
} from "@/src/types/map";

export interface MapCanvasHandle {
  fitMap: () => void;
  exportPng: () => Promise<Blob>;
}

interface MapCanvasProps {
  runMode?: boolean;
}

interface PointerPosition { x: number; y: number }

type Interaction =
  | { kind: "idle" }
  | { kind: "pan"; last: Point }
  | { kind: "shape"; start: Point; current: Point; tool: ShapeTool | "erase"; target: "floor" | "patch" }
  | { kind: "terrain"; lastKey: string; brush: TerrainBrush }
  | { kind: "measure"; start: Point; current: Point }
  | { kind: "objectMove"; id: string; offset: Point; placement: PlacementMode; placing: boolean; started: boolean }
  | { kind: "objectRotate"; id: string; center: Point; startAngle: number; startRotation: number; started: boolean }
  | { kind: "objectResize"; id: string; center: Point; startDistance: number; startWidth: number; startHeight: number; started: boolean }
  | { kind: "pinch"; distance: number; worldCenter: Point; viewport: Viewport };

type TransformHandle = "rotate" | "resize";

const pointerDistance = (a: PointerPosition, b: PointerPosition) =>
  Math.hypot(a.x - b.x, a.y - b.y);

const pointDistance = (a: Point, b: Point) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/**
 * Corner sign pairs in (x, y) order, typed as Point so destructuring yields
 * numbers. A bare array literal widens to number[][] and hides the reads.
 */
const CORNER_SIGNS: readonly Point[] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

const rotatePoint = (point: Point, degrees: number): Point => {
  const radians = (degrees * Math.PI) / 180;
  return [
    point[0] * Math.cos(radians) - point[1] * Math.sin(radians),
    point[0] * Math.sin(radians) + point[1] * Math.cos(radians),
  ];
};

const objectCenter = (object: MapObject): Point => [
  object.x + object.width / 2,
  object.y + object.height / 2,
];

const transformHandleAt = (
  screenPoint: Point,
  object: MapObject,
  viewport: Viewport,
): TransformHandle | null => {
  if (object.locked) return null;
  const center = objectCenter(object);
  const handleWorldOffset = 32 / viewport.zoom;
  const rotateOffset = rotatePoint([0, -object.height / 2 - handleWorldOffset], object.rotation);
  const rotateScreen = Camera.worldToScreen(
    [center[0] + rotateOffset[0], center[1] + rotateOffset[1]],
    viewport,
  );
  if (pointDistance(screenPoint, rotateScreen) <= 15) return "rotate";

  const corners: Point[] = CORNER_SIGNS.map(([x, y]) => {
    const offset = rotatePoint([x * object.width / 2, y * object.height / 2], object.rotation);
    return Camera.worldToScreen([center[0] + offset[0], center[1] + offset[1]], viewport);
  });
  return corners.some((corner) => pointDistance(screenPoint, corner) <= 15)
    ? "resize"
    : null;
};

const pointerCenter = (a: PointerPosition, b: PointerPosition): Point => [
  (a.x + b.x) / 2,
  (a.y + b.y) / 2,
];

const canvasPoint = (
  event: React.PointerEvent<HTMLCanvasElement> | PointerEvent | MouseEvent,
  canvas: HTMLCanvasElement,
): Point => {
  const bounds = canvas.getBoundingClientRect();
  return [event.clientX - bounds.left, event.clientY - bounds.top];
};

const polygonForDrag = (
  tool: ShapeTool | "erase",
  start: Point,
  current: Point,
  gridSize: number,
): Polygon => {
  if (tool === "corridor") return corridorToPolygon(start, current, gridSize);
  const rect = normaliseRect(start, current);
  if (tool === "ellipse") return ellipseToPolygon(rect);
  if (tool === "hexagon") return hexagonToPolygon(rect);
  return rectToPolygon(rect);
};

const validDrag = (
  tool: ShapeTool | "erase",
  start: Point,
  current: Point,
  gridSize: number,
) => tool === "corridor"
  ? pointDistance(start, current) >= gridSize
  : Math.abs(current[0] - start[0]) >= gridSize &&
    Math.abs(current[1] - start[1]) >= gridSize;

const hitObject = (point: Point, objects: MapObject[]): MapObject | null => {
  const ordered = [...objects].sort((a, b) => b.zIndex - a.zIndex);
  return ordered.find((object) => {
    const centerX = object.x + object.width / 2;
    const centerY = object.y + object.height / 2;
    const radians = (-object.rotation * Math.PI) / 180;
    const deltaX = point[0] - centerX;
    const deltaY = point[1] - centerY;
    const localX = deltaX * Math.cos(radians) - deltaY * Math.sin(radians);
    const localY = deltaX * Math.sin(radians) + deltaY * Math.cos(radians);
    return Math.abs(localX) <= object.width / 2 + 5 &&
      Math.abs(localY) <= object.height / 2 + 5;
  }) ?? null;
};

const measurementLabel = (
  start: Point,
  end: Point,
  gridSize: number,
  gridType: "square" | "hex",
) => {
  let cells: number;
  let unit: string;
  if (gridType === "hex") {
    const from = worldToAxial(start, gridSize);
    const to = worldToAxial(end, gridSize);
    const dq = to.q - from.q;
    const dr = to.r - from.r;
    cells = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
    unit = translateStatic("measure.hex", { count: cells });
  } else {
    cells = Math.hypot(end[0] - start[0], end[1] - start[1]) / gridSize;
    cells = Math.round(cells * 10) / 10;
    unit = translateStatic("measure.square", { count: cells });
  }
  const feet = Math.round(cells * 5 * 10) / 10;
  return translateStatic("measure.readout", { feet, count: cells, unit });
};

export const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(function MapCanvas({ runMode = false }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, PointerPosition>());
  const interaction = useRef<Interaction>({ kind: "idle" });
  const firstLayout = useRef(true);
  const previousMode = useRef<"square" | "hex" | null>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  const [previewPolygon, setPreviewPolygon] = useState<Polygon | null>(null);
  const [polygonPoints, setPolygonPoints] = useState<Point[]>([]);
  const [polygonCursor, setPolygonCursor] = useState<Point | null>(null);
  const [spacePressed, setSpacePressed] = useState(false);
  const [hoverHandle, setHoverHandle] = useState<TransformHandle | null>(null);
  const [hoverObjectId, setHoverObjectId] = useState<string | null>(null);
  const [measurement, setMeasurement] = useState<{ start: Point; end: Point } | null>(null);
  const [hoverHex, setHoverHex] = useState<{ q: number; r: number } | null>(null);
  const [contextExpanded, setContextExpanded] = useState(false);

  const tool = useMapStore((state) => state.tool);
  const t = useT();
  const meta = useMapStore((state) => state.meta);
  const patchShape = useMapStore((state) => state.patchShape);
  const patchEraseMode = useMapStore((state) => state.patchEraseMode);
  const terrainPatches = useMapStore((state) => state.terrainPatches);
  const commitTerrainPatch = useMapStore((state) => state.commitTerrainPatch);
  const eraseTerrainPatchAt = useMapStore((state) => state.eraseTerrainPatchAt);
  const geometry = useMapStore((state) => state.floorPolygons);
  const hexCells = useMapStore((state) => state.hexCells);
  const objects = useMapStore((state) => state.objects);
  const selectedObjectId = useMapStore((state) => state.selectedObjectId);
  const selectedStampAsset = useMapStore((state) => state.selectedStampAsset);
  const selectedTerrain = useMapStore((state) => state.selectedTerrain);
  const viewport = useMapStore((state) => state.viewport);
  const setViewport = useMapStore((state) => state.setViewport);
  const commitPolygon = useMapStore((state) => state.commitPolygon);
  const beginTerrainStroke = useMapStore((state) => state.beginTerrainStroke);
  const paintHex = useMapStore((state) => state.paintHex);
  const addObject = useMapStore((state) => state.addObject);
  const addAnnotation = useMapStore((state) => state.addAnnotation);
  const selectObject = useMapStore((state) => state.selectObject);
  const beginObjectTransform = useMapStore((state) => state.beginObjectTransform);
  const updateObject = useMapStore((state) => state.updateObject);
  const updateSelectedObjectDetails = useMapStore((state) => state.updateSelectedObjectDetails);
  const rotateSelectedObject = useMapStore((state) => state.rotateSelectedObject);
  const resizeSelectedObject = useMapStore((state) => state.resizeSelectedObject);
  const setSelectedObjectSize = useMapStore((state) => state.setSelectedObjectSize);
  const changeSelectedObjectLayer = useMapStore((state) => state.changeSelectedObjectLayer);
  const duplicateSelectedObject = useMapStore((state) => state.duplicateSelectedObject);
  const deleteSelectedObject = useMapStore((state) => state.deleteSelectedObject);
  const toggleSelectedObjectLock = useMapStore((state) => state.toggleSelectedObjectLock);
  const openRoomKeyPanel = useMapStore((state) => state.openRoomKeyPanel);
  const activeGeometry = useMemo(() => floorForMode(geometry, meta.gridType), [geometry, meta.gridType]);
  const activeHexCells = useMemo(() => hexesForMode(hexCells, meta.gridType), [hexCells, meta.gridType]);
  const activeObjects = useMemo(() => objectsForMode(objects, meta.gridType), [objects, meta.gridType]);
  const interactionObjects = useMemo(
    () => runMode ? activeObjects.filter((object) => object.assetId.startsWith("token_")) : activeObjects,
    [activeObjects, runMode],
  );
  const selectedObject = interactionObjects.find((object) => object.id === selectedObjectId) ?? null;

  const selectedIsAnnotation = selectedObject?.assetId === "text_label" || selectedObject?.assetId === "room_number";
  const contextualToolbar = selectedObject && tool === "select"
    ? (() => {
        const center = Camera.worldToScreen(objectCenter(selectedObject), viewport);
        const radius = Math.hypot(selectedObject.width, selectedObject.height) * viewport.zoom / 2;
        const toolbarHeight = contextExpanded ? 188 : 76;
        const preferredY = center[1] + radius + 16;
        const y = preferredY <= size.height - toolbarHeight
          ? preferredY
          : Math.max(8, center[1] - radius - toolbarHeight);
        const x = size.width < 340
          ? size.width / 2
          : Math.min(size.width - 165, Math.max(165, center[0]));
        return { x, y };
      })()
    : null;

  useEffect(() => setContextExpanded(false), [selectedObjectId]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const width = Math.max(1, Math.floor(entry.contentRect.width));
      const height = Math.max(1, Math.floor(entry.contentRect.height));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setSize((current) => current.width === width && current.height === height
          ? current
          : { width, height });
        if (firstLayout.current) {
          firstLayout.current = false;
          const bounds = mapContentBounds(activeGeometry, activeObjects, activeHexCells, meta.gridSizeWorld, terrainPatches);
          const mapWidth = Math.max(250, bounds.maxX - bounds.minX + 140);
          const mapHeight = Math.max(250, bounds.maxY - bounds.minY + 140);
          const zoom = Camera.clampZoom(Math.min(width / mapWidth, height / mapHeight, 1.25));
          setViewport({
            zoom,
            x: width / 2 - ((bounds.minX + bounds.maxX) / 2) * zoom,
            y: height / 2 - ((bounds.minY + bounds.maxY) / 2) * zoom,
          });
        }
      });
    });
    observer.observe(node);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [activeGeometry, activeHexCells, activeObjects, meta.gridSizeWorld, setViewport, terrainPatches]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(size.width * scale);
    canvas.height = Math.floor(size.height * scale);
    const context = canvas.getContext("2d");
    if (!context) return;
    renderMapScene({
      context,
      width: size.width,
      height: size.height,
      deviceScale: scale,
      viewport,
      meta,
      geometry: activeGeometry,
      hexCells: activeHexCells,
      objects: activeObjects,
      selectedObjectId,
      preview: previewPolygon
        ? { polygon: previewPolygon, mode: tool === "erase" ? "subtract" : "add" }
        : null,
      draftPolygon: tool === "polygon" || (tool === "patch" && patchShape === "polygon")
        ? { points: polygonPoints, cursor: polygonCursor }
        : null,
      measurement: measurement
        ? {
            start: measurement.start,
            end: measurement.end,
            label: measurementLabel(measurement.start, measurement.end, meta.gridSizeWorld, meta.gridType),
          }
        : null,
      hexBrushPreview: tool === "terrain" && hoverHex
        ? {
            ...hoverHex,
            mode: interaction.current.kind === "terrain"
              ? interaction.current.brush === "erase" ? "erase" : "paint"
              : selectedTerrain === "erase" ? "erase" : "paint",
          }
        : null,
    });
  }, [activeGeometry, activeHexCells, activeObjects, hoverHex, measurement, meta, patchShape, polygonCursor, polygonPoints, previewPolygon, selectedObjectId, selectedTerrain, size, terrainPatches, tool, viewport]);

  useEffect(() => {
    if (tool !== "polygon") {
      setPolygonPoints([]);
      setPolygonCursor(null);
    }
  }, [tool]);

  useEffect(() => {
    if (tool !== "measure") setMeasurement(null);
  }, [tool]);

  useEffect(() => {
    if (tool !== "terrain") setHoverHex(null);
  }, [tool]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space" && event.target === document.body) {
        event.preventDefault();
        setSpacePressed(true);
      }
      const drawingPolygon = tool === "polygon" || (tool === "patch" && patchShape === "polygon");
      if (drawingPolygon && polygonPoints.length > 0) {
        if (event.key === "Escape") {
          setPolygonPoints([]);
          setPolygonCursor(null);
        } else if (event.key === "Backspace") {
          event.preventDefault();
          setPolygonPoints((points) => points.slice(0, -1));
        } else if (event.key === "Enter" && polygonPoints.length >= 3) {
          event.preventDefault();
          if (tool === "patch") commitTerrainPatch(pointsToPolygon(polygonPoints));
          else commitPolygon(pointsToPolygon(polygonPoints), "add");
          setPolygonPoints([]);
          setPolygonCursor(null);
        }
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") setSpacePressed(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [commitPolygon, commitTerrainPatch, patchShape, polygonPoints, tool]);

  const fitMap = useCallback(() => {
    const bounds = mapContentBounds(activeGeometry, activeObjects, activeHexCells, meta.gridSizeWorld, terrainPatches);
    const mapWidth = Math.max(250, bounds.maxX - bounds.minX + 160);
    const mapHeight = Math.max(250, bounds.maxY - bounds.minY + 160);
    const zoom = Camera.clampZoom(Math.min(size.width / mapWidth, size.height / mapHeight, 1.5));
    setViewport({
      zoom,
      x: size.width / 2 - ((bounds.minX + bounds.maxX) / 2) * zoom,
      y: size.height / 2 - ((bounds.minY + bounds.maxY) / 2) * zoom,
    });
  }, [activeGeometry, activeHexCells, activeObjects, meta.gridSizeWorld, setViewport, size, terrainPatches]);

  useEffect(() => {
    if (previousMode.current === null) {
      previousMode.current = meta.gridType;
      return;
    }
    if (previousMode.current !== meta.gridType) {
      previousMode.current = meta.gridType;
      const frame = window.requestAnimationFrame(fitMap);
      return () => window.cancelAnimationFrame(frame);
    }
  }, [fitMap, meta.gridType]);

  useImperativeHandle(ref, () => ({
    fitMap,
    exportPng: async () => {
      const bounds = mapContentBounds(activeGeometry, activeObjects, activeHexCells, meta.gridSizeWorld, terrainPatches);
      const padding = 90;
      const worldWidth = Math.max(500, bounds.maxX - bounds.minX + padding * 2);
      const worldHeight = Math.max(500, bounds.maxY - bounds.minY + padding * 2);
      const scale = Math.min(3, 6000 / Math.max(worldWidth, worldHeight));
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = Math.max(1, Math.floor(worldWidth * scale));
      exportCanvas.height = Math.max(1, Math.floor(worldHeight * scale));
      const context = exportCanvas.getContext("2d");
      if (!context) throw new Error("Could not create the export image.");
      renderMapScene({
        context,
        width: worldWidth,
        height: worldHeight,
        deviceScale: scale,
        viewport: { x: padding - bounds.minX, y: padding - bounds.minY, zoom: 1 },
        meta,
        geometry: activeGeometry,
        hexCells: activeHexCells,
        objects: activeObjects,
        terrainPatches,
        showVignette: false,
      });
      return new Promise<Blob>((resolve, reject) => {
        exportCanvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error("PNG export failed.")),
          "image/png",
        );
      });
    },
  }), [activeGeometry, activeHexCells, activeObjects, fitMap, meta, terrainPatches]);

  const beginPinch = () => {
    const [first, second] = [...pointers.current.values()];
    if (!first || !second) return;
    const center = pointerCenter(first, second);
    interaction.current = {
      kind: "pinch",
      distance: Math.max(1, pointerDistance(first, second)),
      worldCenter: Camera.screenToWorld(center, viewport),
      viewport,
    };
    setPreviewPolygon(null);
  };

  /**
   * The lattice for drawing geometry — room corners, polygon points, label
   * anchors, patch shapes. Grid intersections, or hex centres on a hex map.
   * Placed objects do NOT use this: where an object's centre belongs depends on
   * its catalogue size (D-024), and `placementCentreFor` answers that.
   */
  const snapGeometry = useCallback(
    (unsnapped: Point): Point => geometryCentre(unsnapped, meta.gridType, meta.gridSizeWorld),
    [meta.gridType, meta.gridSizeWorld],
  );

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    const point = canvasPoint(event, canvas);
    pointers.current.set(event.pointerId, { x: point[0], y: point[1] });

    if (pointers.current.size >= 2) {
      beginPinch();
      return;
    }

    if (tool === "pan" || event.button === 1 || spacePressed) {
      interaction.current = { kind: "pan", last: point };
      return;
    }
    if (event.button !== 0 && !(tool === "terrain" && event.button === 2)) return;

    const worldUnsnapped = Camera.screenToWorld(point, viewport);
    const world = snapGeometry(worldUnsnapped);

    if (tool === "terrain") {
      const hex = worldToAxial(worldUnsnapped, meta.gridSizeWorld);
      const brush = event.button === 2 ? "erase" : selectedTerrain;
      beginTerrainStroke();
      paintHex(hex.q, hex.r, brush);
      interaction.current = { kind: "terrain", lastKey: hexKey(hex), brush };
      return;
    }

    if (tool === "measure") {
      interaction.current = { kind: "measure", start: world, current: world };
      setMeasurement({ start: world, end: world });
      return;
    }

    if (tool === "select") {
      const handle = selectedObject
        ? transformHandleAt(point, selectedObject, viewport)
        : null;
      if (selectedObject && handle === "rotate") {
        const center = objectCenter(selectedObject);
        interaction.current = {
          kind: "objectRotate",
          id: selectedObject.id,
          center,
          startAngle: Math.atan2(worldUnsnapped[1] - center[1], worldUnsnapped[0] - center[0]),
          startRotation: selectedObject.rotation,
          started: false,
        };
        return;
      }
      if (selectedObject && handle === "resize") {
        const center = objectCenter(selectedObject);
        interaction.current = {
          kind: "objectResize",
          id: selectedObject.id,
          center,
          startDistance: Math.max(1, pointDistance(worldUnsnapped, center)),
          startWidth: selectedObject.width,
          startHeight: selectedObject.height,
          started: false,
        };
        return;
      }
      const object = hitObject(worldUnsnapped, interactionObjects);
      selectObject(object?.id ?? null);
      if (object && !object.locked) {
        interaction.current = {
          kind: "objectMove",
          id: object.id,
          offset: [worldUnsnapped[0] - object.x, worldUnsnapped[1] - object.y],
          placement: placementModeFor(object.assetId, object),
          placing: false,
          started: false,
        };
      }
      return;
    }

    if (tool === "stamp") {
      const id = addObject(selectedStampAsset, world[0], world[1]);
      // The store sizes the object from its family catalogue, which is not the
      // generic stamp size for windows, arrow slits or double doors. Read the
      // placed object back rather than re-deriving a size that can disagree —
      // and take its placement mode from the same catalogue entry.
      const placed = useMapStore.getState().objects.find((object) => object.id === id);
      const width = placed?.width ?? getStampDefinition(selectedStampAsset).widthCells * meta.gridSizeWorld;
      const height = placed?.height ?? getStampDefinition(selectedStampAsset).heightCells * meta.gridSizeWorld;
      const asset = placed?.assetId ?? selectedStampAsset;
      const placement = placementModeFor(asset, placed ?? {});
      if (placement === "wall-snap") {
        const snapped = wallPlacementFor(
          worldUnsnapped, asset, placed ?? {}, width, height,
          activeGeometry, meta.gridType, meta.gridSizeWorld,
        );
        updateObject(id, { x: snapped.x, y: snapped.y, rotation: snapped.rotation });
      } else {
        // Grid-snapped: the centre lands on the lattice its catalogue size implies,
        // so a 1-cell crate fills a cell and a 2-cell table spans two (D-024).
        const centre = placementCentreFor(
          worldUnsnapped, asset, placed ?? {}, meta.gridType, meta.gridSizeWorld,
        );
        updateObject(id, { x: centre[0] - width / 2, y: centre[1] - height / 2 });
      }
      interaction.current = {
        kind: "objectMove",
        id,
        offset: [width / 2, height / 2],
        placement,
        placing: true,
        started: false,
      };
      return;
    }

    if (tool === "label") {
      addAnnotation(world[0], world[1]);
      return;
    }

    if (tool === "patch") {
      if (patchEraseMode) {
        eraseTerrainPatchAt(worldUnsnapped[0], worldUnsnapped[1]);
        return;
      }
      if (patchShape === "polygon") {
        const first = polygonPoints[0];
        if (first && polygonPoints.length >= 3 && pointDistance(first, world) < meta.gridSizeWorld * 0.2) {
          commitTerrainPatch(pointsToPolygon(polygonPoints));
          setPolygonPoints([]);
          setPolygonCursor(null);
        } else if (!polygonPoints.at(-1) || pointDistance(polygonPoints.at(-1) as Point, world) > 0) {
          setPolygonPoints((points) => [...points, world]);
        }
        return;
      }
      // Rectangle and ellipse reuse the floor tools' drag interaction verbatim.
      const dragTool: ShapeTool = patchShape === "ellipse" ? "ellipse" : "room";
      interaction.current = { kind: "shape", start: world, current: world, tool: dragTool, target: "patch" };
      setPreviewPolygon(polygonForDrag(dragTool, world, world, meta.gridSizeWorld));
      return;
    }

    if (tool === "polygon") {
      const first = polygonPoints[0];
      if (first && polygonPoints.length >= 3 && pointDistance(first, world) < meta.gridSizeWorld * 0.2) {
        commitPolygon(pointsToPolygon(polygonPoints), "add");
        setPolygonPoints([]);
        setPolygonCursor(null);
      } else if (!polygonPoints.at(-1) || pointDistance(polygonPoints.at(-1) as Point, world) > 0) {
        setPolygonPoints((points) => [...points, world]);
      }
      return;
    }

    const shapeTool = tool as ShapeTool | "erase";
    interaction.current = { kind: "shape", start: world, current: world, tool: shapeTool, target: "floor" };
    setPreviewPolygon(polygonForDrag(shapeTool, world, world, meta.gridSizeWorld));
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = canvasPoint(event, canvas);
    if (tool === "terrain") {
      setHoverHex(worldToAxial(Camera.screenToWorld(point, viewport), meta.gridSizeWorld));
    }
    if (tool === "polygon" || (tool === "patch" && patchShape === "polygon")) {
      const world = Camera.screenToWorld(point, viewport);
      setPolygonCursor(meta.gridType === "hex"
        ? axialToWorld(worldToAxial(world, meta.gridSizeWorld), meta.gridSizeWorld)
        : snapPointToGrid(world, meta.gridSizeWorld));
    }
    if (!pointers.current.has(event.pointerId)) {
      const hovered = hitObject(Camera.screenToWorld(point, viewport), interactionObjects);
      setHoverObjectId(hovered?.id ?? null);
      setHoverHandle(
        tool === "select" && selectedObject
          ? transformHandleAt(point, selectedObject, viewport)
          : null,
      );
      return;
    }
    pointers.current.set(event.pointerId, { x: point[0], y: point[1] });

    if (pointers.current.size >= 2) {
      const active = interaction.current;
      if (active.kind !== "pinch") {
        beginPinch();
        return;
      }
      const [first, second] = [...pointers.current.values()];
      if (!first || !second) return;
      const center = pointerCenter(first, second);
      const nextZoom = Camera.clampZoom(
        active.viewport.zoom * (pointerDistance(first, second) / active.distance),
      );
      setViewport({
        zoom: nextZoom,
        x: center[0] - active.worldCenter[0] * nextZoom,
        y: center[1] - active.worldCenter[1] * nextZoom,
      });
      return;
    }

    const active = interaction.current;
    if (active.kind === "pan") {
      setViewport(Camera.pan(viewport, [point[0] - active.last[0], point[1] - active.last[1]]));
      interaction.current = { kind: "pan", last: point };
    } else if (active.kind === "shape") {
      const unsnapped = Camera.screenToWorld(point, viewport);
      const world = meta.gridType === "hex"
        ? axialToWorld(worldToAxial(unsnapped, meta.gridSizeWorld), meta.gridSizeWorld)
        : snapPointToGrid(unsnapped, meta.gridSizeWorld);
      interaction.current = { ...active, current: world };
      setPreviewPolygon(polygonForDrag(active.tool, active.start, world, meta.gridSizeWorld));
    } else if (active.kind === "terrain") {
      const hex = worldToAxial(Camera.screenToWorld(point, viewport), meta.gridSizeWorld);
      const key = hexKey(hex);
      if (key !== active.lastKey) {
        paintHex(hex.q, hex.r, active.brush);
        interaction.current = { kind: "terrain", lastKey: key, brush: active.brush };
      }
    } else if (active.kind === "measure") {
      const unsnapped = Camera.screenToWorld(point, viewport);
      const world = meta.gridType === "hex"
        ? axialToWorld(worldToAxial(unsnapped, meta.gridSizeWorld), meta.gridSizeWorld)
        : snapPointToGrid(unsnapped, meta.gridSizeWorld);
      interaction.current = { ...active, current: world };
      setMeasurement({ start: active.start, end: world });
    } else if (active.kind === "objectMove") {
      const world = Camera.screenToWorld(point, viewport);
      const snap = meta.gridSizeWorld / 2;
      if (!active.started) {
        beginObjectTransform();
        interaction.current = { ...active, started: true };
      }
      if (active.placement === "wall-snap") {
        // Wall objects follow the pointer projected onto the nearest wall, for
        // the whole drag. Half-grid snapping them here is what used to undo the
        // snap taken on the placing click (D-022).
        const moved = useMapStore.getState().objects.find((object) => object.id === active.id);
        const width = moved?.width ?? active.offset[0] * 2;
        const height = moved?.height ?? active.offset[1] * 2;
        const placement = wallPlacementFor(
          world, moved?.assetId, moved ?? {}, width, height,
          activeGeometry, meta.gridType, meta.gridSizeWorld,
        );
        updateObject(active.id, {
          x: placement.x,
          y: placement.y,
          ...(placement.snapped ? { rotation: placement.rotation } : {}),
        });
      } else if (active.placing) {
        // Still finishing the placing gesture: keep the object centred on the
        // pointer, on the same lattice the click used — derived from the object's
        // own catalogue entry, so the drag steps a whole cell at a time and the
        // alignment it was placed with is preserved (D-022, D-024).
        const moved = useMapStore.getState().objects.find((object) => object.id === active.id);
        const width = moved?.width ?? active.offset[0] * 2;
        const height = moved?.height ?? active.offset[1] * 2;
        const centre = placementCentreFor(
          world, moved?.assetId, moved ?? {}, meta.gridType, meta.gridSizeWorld,
        );
        updateObject(active.id, { x: centre[0] - width / 2, y: centre[1] - height / 2 });
      } else {
        updateObject(active.id, {
          x: snapToGrid(world[0] - active.offset[0], snap),
          y: snapToGrid(world[1] - active.offset[1], snap),
        });
      }
    } else if (active.kind === "objectRotate") {
      const world = Camera.screenToWorld(point, viewport);
      if (!active.started) {
        beginObjectTransform();
        interaction.current = { ...active, started: true };
      }
      const angle = Math.atan2(world[1] - active.center[1], world[0] - active.center[0]);
      const rawRotation = active.startRotation + ((angle - active.startAngle) * 180) / Math.PI;
      const snap = event.altKey ? 1 : event.shiftKey ? 90 : 15;
      updateObject(active.id, { rotation: (Math.round(rawRotation / snap) * snap + 360) % 360 });
    } else if (active.kind === "objectResize") {
      const world = Camera.screenToWorld(point, viewport);
      if (!active.started) {
        beginObjectTransform();
        interaction.current = { ...active, started: true };
      }
      const scale = pointDistance(world, active.center) / active.startDistance;
      const snap = meta.gridSizeWorld / 4;
      const width = Math.max(snap, snapToGrid(active.startWidth * scale, snap));
      const height = Math.max(snap, snapToGrid(active.startHeight * scale, snap));
      updateObject(active.id, {
        width,
        height,
        x: active.center[0] - width / 2,
        y: active.center[1] - height / 2,
      });
    }
  };

  const endPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const active = interaction.current;
    pointers.current.delete(event.pointerId);
    if (active.kind === "shape" && pointers.current.size === 0 &&
      validDrag(active.tool, active.start, active.current, meta.gridSizeWorld)) {
      const polygon = polygonForDrag(active.tool, active.start, active.current, meta.gridSizeWorld);
      if (active.target === "patch") commitTerrainPatch(polygon);
      else commitPolygon(polygon, active.tool === "erase" ? "subtract" : "add");
    }
    if (pointers.current.size === 0) {
      interaction.current = { kind: "idle" };
      setPreviewPolygon(null);
      setHoverHandle(null);
      setHoverObjectId(null);
    } else if (pointers.current.size === 1) {
      interaction.current = { kind: "idle" };
    }
  };

  /**
   * Opens the room key for a room number.
   *
   * Deliberately a `dblclick` listener rather than a second state machine inside
   * the pointer handlers: the two clicks it is built from still select and (if
   * the pointer moves) drag exactly as before, so single-click behaviour is
   * untouched by construction. Select tool only — double-clicking while a build
   * tool is active is part of a drawing gesture. Touch double-tap is Phase 4.
   */
  const onDoubleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (runMode || tool !== "select") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const world = Camera.screenToWorld(canvasPoint(event.nativeEvent, canvas), viewport);
    const object = hitObject(world, interactionObjects);
    if (object?.assetId === ROOM_KEY_ANCHOR_ASSET) openRoomKeyPanel(object.id);
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = canvasPoint(event.nativeEvent, canvas);
    const factor = Math.exp(-event.deltaY * 0.0014);
    setViewport(Camera.zoomAt(viewport, point, viewport.zoom * factor));
  };

  const cursor = tool === "pan" || spacePressed
    ? interaction.current.kind === "pan" ? "grabbing" : "grab"
    : hoverHandle === "rotate" ? "grab"
    : hoverHandle === "resize" ? "nwse-resize"
    : tool === "select" && hoverObjectId ? "move"
    : tool === "select" ? "default"
    : tool === "terrain" ? "cell"
    : tool === "stamp" ? "copy"
    : tool === "patch" ? (patchEraseMode ? "cell" : "crosshair")
    : "crosshair";

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-[#d8c7a8]">
      <canvas
        ref={canvasRef}
        className="block h-full w-full select-none touch-none"
        style={{ cursor }}
        aria-label={meta.gridType === "hex" ? t("object.canvasHex") : t("object.canvasDungeon")}
        onContextMenu={(event) => event.preventDefault()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onDoubleClick={onDoubleClick}
        onPointerLeave={() => { if (tool === "polygon" || tool === "patch") setPolygonCursor(null); setHoverHex(null); }}
        onWheel={onWheel}
      />
      {!runMode && selectedObject && contextualToolbar && (
        <div
          className="absolute z-20 flex max-w-[calc(100%_-_16px)] flex-col rounded-xl border border-[#33281f]/20 bg-[#201e1a]/96 p-1.5 text-stone-100 shadow-2xl shadow-black/30 backdrop-blur"
          style={{ left: contextualToolbar.x, top: contextualToolbar.y, transform: "translateX(-50%)" }}
          onPointerDown={(event) => event.stopPropagation()}
          role="toolbar"
          aria-label={t("object.quickActions", { label: t(getStampDefinition(selectedObject.assetId).labelKey) })}
        >
          <div className="flex items-center justify-between gap-4 px-2 pb-1 text-[9px]">
            <span className="max-w-28 truncate font-semibold text-stone-300">{t("object.selected", { label: t(getStampDefinition(selectedObject.assetId).labelKey) })}</span>
            {!selectedObject.locked && <span className="flex items-center gap-1 whitespace-nowrap font-semibold text-[#ee8b80]"><Move size={11} /> {t("object.dragToMove")}</span>}
          </div>
          <div className="flex items-center gap-1">
            {!selectedObject.locked && (
              <>
                <button type="button" onClick={() => rotateSelectedObject(-15)} className="grid h-9 w-9 place-items-center rounded-lg text-stone-300 transition hover:bg-white/10 hover:text-white" aria-label={t("object.rotateLeft")} title={t("object.rotateLeftTitle")}><RotateCcw size={16} /></button>
                <span className="min-w-9 text-center text-[10px] font-bold tabular-nums text-[#ee8b80]">{Math.round(selectedObject.rotation)}°</span>
                <button type="button" onClick={() => rotateSelectedObject(15)} className="grid h-9 w-9 place-items-center rounded-lg text-stone-300 transition hover:bg-white/10 hover:text-white" aria-label={t("object.rotateRight")} title={t("object.rotateRightTitle")}><RotateCw size={16} /></button>
                <span className="mx-0.5 h-5 w-px bg-white/10" />
                <button type="button" onClick={duplicateSelectedObject} className="grid h-9 w-9 place-items-center rounded-lg text-stone-300 transition hover:bg-white/10 hover:text-white" aria-label={t("object.duplicate")} title={t("object.duplicateTitle")}><Copy size={16} /></button>
              </>
            )}
            <button type="button" onClick={toggleSelectedObjectLock} className="grid h-9 w-9 place-items-center rounded-lg text-stone-300 transition hover:bg-white/10 hover:text-white" aria-label={selectedObject.locked ? t("object.unlock") : t("object.lock")} title={selectedObject.locked ? t("object.unlock") : t("object.lock")}>{selectedObject.locked ? <Lock size={16} /> : <LockOpen size={16} />}</button>
            <button type="button" onClick={deleteSelectedObject} className="grid h-9 w-9 place-items-center rounded-lg text-stone-400 transition hover:bg-red-500/15 hover:text-red-300" aria-label={t("object.delete")} title={t("object.deleteTitle")}><Trash2 size={16} /></button>
            {!selectedObject.locked && <button type="button" onClick={() => setContextExpanded((expanded) => !expanded)} className={`grid h-9 w-9 place-items-center rounded-lg transition hover:bg-white/10 hover:text-white ${contextExpanded ? "bg-white/10 text-white" : "text-stone-400"}`} aria-label={t("object.more")} aria-expanded={contextExpanded} title={t("object.moreTitle")}><MoreHorizontal size={17} /></button>}
          </div>
          {contextExpanded && !selectedObject.locked && (
            <div className="mt-1 border-t border-white/10 pt-1.5">
              <div className="grid grid-cols-4 gap-1">
                <button type="button" onClick={() => resizeSelectedObject(0.8)} className="flex h-9 items-center justify-center gap-1 rounded-lg text-[9px] font-semibold text-stone-400 transition hover:bg-white/10 hover:text-white" aria-label={t("object.makeSmaller")}><Minus size={13} /> {t("object.smaller")}</button>
                <button type="button" onClick={() => resizeSelectedObject(1.25)} className="flex h-9 items-center justify-center gap-1 rounded-lg text-[9px] font-semibold text-stone-400 transition hover:bg-white/10 hover:text-white" aria-label={t("object.makeLarger")}><Plus size={13} /> {t("object.larger")}</button>
                <button type="button" onClick={() => changeSelectedObjectLayer(-1)} className="flex h-9 items-center justify-center gap-1 rounded-lg text-[9px] font-semibold text-stone-400 transition hover:bg-white/10 hover:text-white" aria-label={t("object.sendBackward")}><ArrowDownToLine size={13} /> {t("object.back")}</button>
                <button type="button" onClick={() => changeSelectedObjectLayer(1)} className="flex h-9 items-center justify-center gap-1 rounded-lg text-[9px] font-semibold text-stone-400 transition hover:bg-white/10 hover:text-white" aria-label={t("object.bringForward")}><ArrowUpToLine size={13} /> {t("object.front")}</button>
              </div>
              {selectedIsAnnotation ? (
                <div className="mt-1.5 grid grid-cols-[1fr_auto_auto] items-end gap-2 rounded-lg bg-white/[0.035] px-2 py-2 text-[9px] text-stone-500">
                  <label className="font-semibold uppercase tracking-wider">{t("object.content")}<input value={selectedObject.text ?? ""} onChange={(event) => updateSelectedObjectDetails({ text: event.target.value })} className="mt-1 h-8 w-full rounded-md border border-white/10 bg-[#2a2722] px-2 text-[10px] font-medium normal-case tracking-normal text-stone-100 outline-none focus:border-[#c34a40]" aria-label={t("object.contentAria")} /></label>
                  <label className="font-semibold uppercase tracking-wider">{t("object.size")}<input type="number" min={10} max={96} value={selectedObject.fontSize ?? 24} onChange={(event) => updateSelectedObjectDetails({ fontSize: Number(event.target.value) || 24 })} className="mt-1 block h-8 w-14 rounded-md border border-white/10 bg-[#2a2722] px-1 text-center text-[10px] font-medium normal-case tracking-normal text-stone-100 outline-none focus:border-[#c34a40]" aria-label={t("object.fontSizeAria")} /></label>
                  <label className="font-semibold uppercase tracking-wider">{t("annotation.color")}<input type="color" value={selectedObject.color ?? "#2d241d"} onChange={(event) => updateSelectedObjectDetails({ color: event.target.value })} className="mt-1 block h-8 w-10 cursor-pointer rounded-md border border-white/10 bg-transparent p-1" aria-label={t("annotation.colorAria")} /></label>
                </div>
              ) : (
                <div className="mt-1.5 flex items-center gap-2 rounded-lg bg-white/[0.035] px-2 py-1.5 text-[9px] text-stone-500">
                  <span className="font-semibold uppercase tracking-wider">{t("object.size")}</span>
                  <label className="flex items-center gap-1">W<input type="number" min={0.25} max={20} step={0.25} value={Number((selectedObject.width / meta.gridSizeWorld).toFixed(2))} onChange={(event) => { const cells = Number(event.target.value); if (Number.isFinite(cells) && cells > 0) setSelectedObjectSize(cells * meta.gridSizeWorld, selectedObject.height); }} className="h-7 w-14 rounded-md border border-white/10 bg-[#2a2722] px-1.5 text-center text-[10px] text-stone-100 outline-none focus:border-[#c34a40]" aria-label={t("object.widthAria")} /></label>
                  <span>×</span>
                  <label className="flex items-center gap-1">H<input type="number" min={0.25} max={20} step={0.25} value={Number((selectedObject.height / meta.gridSizeWorld).toFixed(2))} onChange={(event) => { const cells = Number(event.target.value); if (Number.isFinite(cells) && cells > 0) setSelectedObjectSize(selectedObject.width, cells * meta.gridSizeWorld); }} className="h-7 w-14 rounded-md border border-white/10 bg-[#2a2722] px-1.5 text-center text-[10px] text-stone-100 outline-none focus:border-[#c34a40]" aria-label={t("object.heightAria")} /></label>
                  <span>{t("object.cells")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
